import { ProductDocument } from 'models/product';
import {
  uploadListingImage,
  uploadOtherImage,
  uploadReviewImage,
} from '@util/firestore/listing';
import { logError } from '@util/logError';
import { getCdnImageUrls } from '@util/urlHelpers';
import { useAuth } from 'context/AuthContext';
import { useState } from 'react';
import { blobToBase64, IMAGE_PATH } from '..';
import { convertHeic } from '@util/heic';
import { anyAreHeic } from '@util/heic';
import { getDownloadURL, ref, uploadString } from 'firebase/storage';
import { storage } from '@util/firebase';

type MediaItem =
  | ({
      type: 'image';
    } & ProductDocument['images'][0])
  | ({
      type: 'video';
    } & ProductDocument['video']);

type UseMultiImageUpload = {
  id?: string;
  onUploadCallback?: (args: {
    paths: ProductDocument['images'];
    errors: string[];
  }) => void;
  onUploadVideoCallback?: (args: {
    path: ProductDocument['video'] | undefined;
    errors: string[];
  }) => void;
  onDeleteCallback?: (args: {
    images: ProductDocument['images'];
    video?: ProductDocument['video'];
  }) => void;
  type: 'listing' | 'review' | 'other';
  product?: ProductDocument;
};

export default function useMultiImageUpload({
  id,
  onUploadCallback,
  onDeleteCallback,
  type = 'listing',
  product,
  onUploadVideoCallback,
}: UseMultiImageUpload) {
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const { user } = useAuth();

  async function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!user) return;

    const files = Array.from(e.target.files ?? []);

    if (!files.length) return;

    const imageFiles: File[] = [];
    const videoFiles: File[] = [];

    files.forEach((file) => {
      if (file.type.includes('image')) {
        imageFiles.push(file);
        return;
      }

      if (file.type.includes('video')) {
        videoFiles.push(file);
        return;
      }
    });

    if (videoFiles.length > 1) {
      console.error('You can upload only 1 video');
      return;
    }

    const hasInvalidFile = files.some(
      (file) => !file.type.includes('image') && !file.type.includes('video')
    );

    if (hasInvalidFile) {
      console.error('Only images and videos are allowed');
      return;
    }

    if (imageFiles.length > 0) {
      const args = await uploadImages(imageFiles);

      if (onUploadCallback) onUploadCallback(args);
    }

    if (videoFiles.length > 0 && product) {
      const video = videoFiles[0];
      const path =
        product.seller_id + '/products/' + product.id + '/image' + Date.now();
      const args = await uploadVideo(video, path);
      if (onUploadVideoCallback) onUploadVideoCallback(args);
    }
  }

  async function uploadImages(images: File[]) {
    if (!user) return { paths: [], errors: ['User not found'] };
    const paths: ProductDocument['images'] = [];
    const errors: string[] = [];

    try {
      setUploadInProgress(true);

      const anyHeic = anyAreHeic(images);
      const { heicTo } = anyHeic ? await import('heic-to') : {};

      const uploadPromises = images.map(async (image) => {
        let file = image;
        // If we want to support HEIC images on frontend, we can use this code. Currently backend does the conversion.
        if (image.type === 'image/heic' && heicTo) {
          try {
            file = await convertHeic(heicTo, image);
          } catch (e) {
            logError(
              e,
              undefined,
              undefined,
              `HEIC conversion failed for ${image.name} ${(
                image.size /
                1024 /
                1024
              ).toFixed(2)}MB`
            );
            errors.push(`Invalid image format for ${image.name}`);
            return null;
          }
        }
        const base64 = await blobToBase64(file);
        if (type === 'listing') {
          const { download_url, remotePath } = await uploadListingImage({
            data: base64,
            productId: id ?? '',
            userId: user.uid,
          });
          return {
            download_url,
            ...getCdnImageUrls(remotePath),
          };
        } else if (type === 'review') {
          const { download_url, remotePath } = await uploadReviewImage(
            base64,
            id ?? ''
          );
          return {
            download_url,
            ...getCdnImageUrls(remotePath),
          };
        } else {
          const { download_url, remotePath } = await uploadOtherImage(
            file,
            id ?? ''
          );
          return {
            download_url,
            mime_type: file.type,
            ...getCdnImageUrls(remotePath),
          };
        }
      });

      const res = await Promise.allSettled(uploadPromises);
      res.forEach((r, i) => {
        if (r.status === 'rejected')
          logError(
            r.reason,
            undefined,
            undefined,
            `uploadImages: ${images[i].type} ${(
              images[i].size /
              1024 /
              1024
            ).toFixed(2)}MB failed`
          );
        else if (r.status === 'fulfilled' && r.value) paths.push(r.value);
      });
    } catch (e) {
      logError(
        e,
        undefined,
        undefined,
        `uploadImages main: ${images.length} failed`
      );
      errors.push('Failed to upload images');
    }
    setUploadInProgress(false);
    return { paths, errors };
  }

  async function deleteMedia(media: MediaItem, currentMedia: MediaItem[]) {
    const updatedMedia = currentMedia.filter(
      (item) => item.thumb !== media.thumb
    );

    const images = updatedMedia.filter(
      (item): item is Extract<MediaItem, { type: 'image' }> =>
        item.type === 'image'
    );

    const video = updatedMedia.find(
      (item): item is Extract<MediaItem, { type: 'video' }> =>
        item.type === 'video'
    );

    onDeleteCallback?.({
      images,
      video,
    });
  }

  const uploadVideo = async (file: File, path: string) => {
    let downloadUrl = '';
    let cdnUrl = '';
    const errors: string[] = [];

    if (!file || !path) {
      return {
        path: {
          download_url: '',
          mime_type: '',
          thumb: '',
          full: '',
        },
        errors: ['Invalid video or path'],
      };
    }

    if (file.name?.startsWith('http')) {
      return {
        path: {
          download_url: file.name,
          mime_type: file.type,
          thumb: file.name,
          full: file.name,
        },
        errors: [],
      };
    }

    const storageRef = ref(storage, path);

    try {
      setUploadInProgress(true);

      const b64 = await blobToBase64(file);

      const res = await uploadString(
        storageRef,
        b64.split('base64,')[1],
        'base64',
        {
          contentType: file.type,
        }
      );

      cdnUrl = IMAGE_PATH + res.metadata.fullPath;
      downloadUrl = await getDownloadURL(res.ref);

      return {
        path: {
          download_url: downloadUrl,
          mime_type: file.type,
          full: cdnUrl,
          thumb: cdnUrl,
        },
        errors: [],
      };
    } catch (e) {
      logError(
        e,
        undefined,
        undefined,
        `uploadVideo failed: ${file.type} ${(file.size / 1024 / 1024).toFixed(
          2
        )}MB`
      );

      errors.push('Failed to upload video');

      return {
        path: {
          download_url: cdnUrl,
          mime_type: file.type,
          full: cdnUrl,
          thumb: cdnUrl,
        },
        errors,
      };
    } finally {
      setUploadInProgress(false);
    }
  };

  return {
    uploadingImage: uploadInProgress,
    uploadImages,
    handleFileInputChange,
    deleteImage: deleteMedia,
    uploadVideo,
  };
}
